export type ProcessedVariant = {
  file: File;
  variant: "sm" | "md" | "lg";
  width: number;
};

export type ResponsiveConfig = {
  sm: { width: number; height?: number };
  md?: { width: number; height?: number };
  lg: { width: number; height?: number };
};

export const BACKGROUND_RESPONSIVE_CONFIG: ResponsiveConfig = {
  sm: { width: 480 },
  md: { width: 1024 },
  lg: { width: 1920 },
};

export const LOGO_RESPONSIVE_CONFIG: ResponsiveConfig = {
  sm: { width: 120, height: 120 },
  lg: { width: 300, height: 300 },
};

/**
 * Creates an HTMLImageElement from a File or URL
 */
export function loadImage(src: File | string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = src instanceof File ? URL.createObjectURL(src) : src;

    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (src instanceof File) {
        URL.revokeObjectURL(url);
      }
      resolve(img);
    };
    img.onerror = (err) => {
      if (src instanceof File) {
        URL.revokeObjectURL(url);
      }
      reject(err);
    };
    img.src = url;
  });
}

/**
 * Converts a Canvas to a WebP File Blob
 */
export function canvasToWebPFile(
  canvas: HTMLCanvasElement,
  fileName: string,
  quality = 0.85
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Error converting canvas to WebP blob"));
          return;
        }
        const cleanName = fileName.replace(/\.[^/.]+$/, "");
        const webpFile = new File([blob], `${cleanName}.webp`, {
          type: "image/webp",
        });
        resolve(webpFile);
      },
      "image/webp",
      quality
    );
  });
}

/**
 * Generates WebP responsive image files from a given source File.
 * Caps ultra-large images (e.g. 8K) at 1920px width for lg.
 */
export async function generateResponsiveWebPVariants(
  file: File,
  type: "logo" | "background" = "background"
): Promise<ProcessedVariant[]> {
  const img = await loadImage(file);
  const config =
    type === "logo" ? LOGO_RESPONSIVE_CONFIG : BACKGROUND_RESPONSIVE_CONFIG;

  const originalWidth = img.naturalWidth || img.width;
  const originalHeight = img.naturalHeight || img.height;

  const variants: ProcessedVariant[] = [];

  const entries = Object.entries(config) as [
    "sm" | "md" | "lg",
    { width: number; height?: number }
  ][];

  for (const [variantKey, { width: targetWidth, height: targetHeight }] of entries) {
    let targetW = targetWidth;
    let targetH = targetHeight;

    if (!targetH) {
      // Calculate height preserving aspect ratio
      const aspectRatio = originalHeight / originalWidth;
      // Cap target width to 1920 max for lg, or original width if smaller
      targetW = Math.min(targetWidth, Math.max(originalWidth, targetWidth));
      if (variantKey === "lg" && targetW > 1920) {
        targetW = 1920;
      }
      targetH = Math.round(targetW * aspectRatio);
    }

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, targetW, targetH);
    }

    const quality = type === "logo" ? 0.92 : 0.85;
    const baseName = `${file.name.replace(/\.[^/.]+$/, "")}_${variantKey}`;
    const webpFile = await canvasToWebPFile(canvas, baseName, quality);

    variants.push({
      file: webpFile,
      variant: variantKey,
      width: targetW,
    });
  }

  return variants;
}

import { storage } from "@/config/firebase.config";
import { ImageProps } from "@/types";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

/**
 * Uploads a single WebP file to Firebase Storage with resumable upload,
 * metadata contentType 'image/webp', and retry logic for poor network connections.
 */
export async function uploadWebPFileToStorage(
  storagePath: string,
  file: File,
  retries = 3
): Promise<string> {
  const storageRef = ref(storage, storagePath);
  const metadata = { contentType: "image/webp" };

  let attempt = 0;
  while (attempt < retries) {
    try {
      return await new Promise<string>((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, file, metadata);

        uploadTask.on(
          "state_changed",
          null,
          (error) => reject(error),
          async () => {
            try {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            } catch (err) {
              reject(err);
            }
          }
        );
      });
    } catch (error) {
      attempt++;
      if (attempt >= retries) {
        throw error;
      }
      await new Promise((res) => setTimeout(res, 1000));
    }
  }
  throw new Error("Upload failed after retries");
}

/**
 * Generates WebP variants (sm, md, lg) and uploads them sequentially to Storage.
 * Returns a fully-formed ImageProps object compatible with Firestore and responsive rendering.
 */
export async function uploadResponsiveWebPImage(
  basePathWithoutExt: string,
  file: File,
  type: "logo" | "background" = "background"
): Promise<ImageProps> {
  const variants = await generateResponsiveWebPVariants(file, type);
  const urls: { sm?: string; md?: string; lg?: string } = {};
  const srcsetParts: string[] = [];

  for (const variant of variants) {
    const path = `${basePathWithoutExt}_${variant.variant}.webp`;
    const downloadURL = await uploadWebPFileToStorage(path, variant.file);

    urls[variant.variant] = downloadURL;
    srcsetParts.push(`${downloadURL} ${variant.width}w`);
  }

  const primaryUrl = urls.lg || urls.md || urls.sm || "";

  return {
    title: file.name,
    alt: file.name,
    url: primaryUrl,
    urls,
    srcset: srcsetParts.join(", "),
  };
}
